import React, { useEffect, useMemo, useRef, useState } from 'react';
import Api from '../../utils/Api/Api';
import Helper from '../../utils/Helper';

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('style[data-phonepe-drawer]')) {
  styleSheet.setAttribute('data-phonepe-drawer', 'true');
  document.head.appendChild(styleSheet);
}

const LOGO_GPAY = 'https://res.cloudinary.com/dhmw8d3ka/image/upload/v1675745747/Payment%20Icons/card%20icons%20%28payment%20revamp%29/BASE-3_qz9u5u.png';
const LOGO_PHONEPE = 'https://res.cloudinary.com/dhmw8d3ka/image/upload/v1675745916/Payment%20Icons/card%20icons%20%28payment%20revamp%29/phonepe_okfpzt.png';
const LOGO_UPI = 'https://res.cloudinary.com/dhmw8d3ka/image/upload/v1675745916/Payment%20Icons/card%20icons%20%28payment%20revamp%29/upi_rpiwyl.png';
const LOGO_PAYTM = "https://download.logo.wine/logo/Paytm/Paytm-Logo.wine.png";

const buildAppIntentUrl = (baseUpiUrl, app) => {
  if (!baseUpiUrl || !baseUpiUrl.startsWith('upi://')) return baseUpiUrl;
  try {
    const url = new URL(baseUpiUrl);
    const params = url.search; // includes leading '?'
    switch (app) {
      case 'phonepe':
        // Production: phonepe://
        // For testing: return `ppesim://pay${params}`;
        return `phonepe://pay${params}`;
      case 'gpay':
        return `gpay://upi/pay${params}`;
      case 'paytm':
        return `paytmmp://pay${params}`;
      case 'bhim':
        return `bhim://upi/pay${params}`;
      default:
        return baseUpiUrl;
    }
  } catch {
    return baseUpiUrl;
  }
};

export const PhonePeDrawer = ({ 
  visible, 
  onClose, 
  orderId, 
  paymentOptions: initialPaymentOptions, 
  paymentId, 
  onSuccess, 
  autoOpenOnMobile = false 
}) => {
  const isMobile = Helper.isMobile();
  const [paymentOptions, setPaymentOptions] = useState(initialPaymentOptions || null);
  const [statusMsg, setStatusMsg] = useState('Scan & pay via any UPI app');
  const [isPolling, setIsPolling] = useState(false);
  const isPollingRef = useRef(false); // Track actual polling state to prevent race conditions
  const attemptsRef = useRef(0);
  const autoOpenedRef = useRef(false);
  const [amount, setAmount] = useState(null);
  const [countdown, setCountdown] = useState(5 * 60); // 5 minutes
  
  // New UI states
  const [paymentState, setPaymentState] = useState('initial'); // initial, payment_in_progress, checking_status, payment_pending, payment_success, payment_failed
  const [lastClickedApp, setLastClickedApp] = useState(null);
  const [hasReturnedFromApp, setHasReturnedFromApp] = useState(false);

  // Lock scroll when open and reset state
  useEffect(() => {
    if (!visible) {
      // Reset state when drawer closes
      setPaymentState('initial');
      setLastClickedApp(null);
      setHasReturnedFromApp(false);
      setIsPolling(false);
      attemptsRef.current = 0;
      return;
    }
    
    // Reset state when drawer opens (new payment)
    setPaymentState('initial');
    setLastClickedApp(null);
    setHasReturnedFromApp(false);
    setStatusMsg('Scan & pay via any UPI app');
    attemptsRef.current = 0;
    
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  // Fetch latest payment info (also gives amount)
  useEffect(() => {
    let mounted = true;
    if (!visible || !orderId) return;
    
    console.log("=== PhonePe Drawer: Fetching Payment Info ===");
    console.log("Order ID:", orderId);
    console.log("Payment ID:", paymentId);
    
    Api.ProcessPaymentV4({ order_id: orderId, payment_id: paymentId })
      .then(({ data }) => {
        if (!mounted || !data) return;
        
        console.log("=== PhonePe Drawer: Payment Info Received ===");
        console.log("Status:", data.status);
        console.log("Is Purchase Done:", data.is_purchase_done);
        console.log("Amount:", data.final_amount_in_inr || data.amount);
        console.log("Payment Options:", JSON.stringify(data.payment_options, null, 2));
        
        // Check if payment is already completed (e.g., page refresh after payment)
        const status = (data.status || '').toLowerCase();
        if (status === 'paid' && data.is_purchase_done) {
          console.log("✅ Payment already completed! Showing success and redirecting...");
          setPaymentState('payment_success');
          setStatusMsg('Payment successful!');
          setTimeout(() => {
            onSuccess?.();
          }, 1500); // Slightly longer delay for already-completed payments
          return;
        }
        
        // Normal flow: Set amount and payment options
        if (data.final_amount_in_inr || data.amount) setAmount(data.final_amount_in_inr || data.amount);
        
        // Only update payment options if we don't have them yet (prevent infinite loops)
        if (data.payment_options && !paymentOptions) {
          setPaymentOptions(data.payment_options);
        } else if (!data.payment_options && !paymentOptions) {
          console.warn("⚠️ No payment options available. Payment might be completed or pending.");
        }
      })
      .catch((err) => {
        console.error("PhonePe Drawer: Error fetching payment info:", err);
      });
    return () => { mounted = false; };
  }, [visible, orderId, paymentId, initialPaymentOptions]);

  // Keep local paymentOptions in sync when parent provides it after open
  useEffect(() => {
    if (initialPaymentOptions) {
      setPaymentOptions(initialPaymentOptions);
    }
  }, [initialPaymentOptions]);
  
  // Reset state when payment ID changes (new payment attempt)
  useEffect(() => {
    if (paymentId) {
      console.log("New payment ID detected, resetting state:", paymentId);
      setPaymentState('initial');
      setLastClickedApp(null);
      setHasReturnedFromApp(false);
      attemptsRef.current = 0;
    }
  }, [paymentId]);

  // Auto-open UPI intent on mobile only once (disabled by default; opt-in via prop)
  useEffect(() => {
    if (!autoOpenOnMobile) return;
    if (!visible || !isMobile || !paymentOptions?.intent_url || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    const t = setTimeout(() => { window.location.href = paymentOptions.intent_url; }, 400);
    return () => clearTimeout(t);
  }, [visible, isMobile, paymentOptions, autoOpenOnMobile]);

  // Start polling - with proper cleanup and race condition prevention
  useEffect(() => {
    console.log("🔄 Polling useEffect triggered - visible:", visible, "orderId:", orderId, "paymentId:", paymentId);
    
    if (!visible || !orderId) {
      console.log("❌ Polling conditions not met - visible:", visible, "orderId:", orderId);
      isPollingRef.current = false;
      setIsPolling(false);
      return;
    }
    
    // Prevent race condition: Check ref instead of state
    if (isPollingRef.current) {
      console.log("⚠️ Polling already active (ref check), skipping duplicate start");
      return;
    }
    
    console.log("=== PhonePe Drawer: Starting NEW Payment Status Polling ===");
    console.log("⏰ Current Time:", new Date().toISOString());
    console.log("📱 Device Type:", isMobile ? "Mobile" : "Desktop");
    console.log("🆔 Order ID:", orderId);
    console.log("💳 Payment ID:", paymentId);
    console.log("🔢 Initial Attempts:", attemptsRef.current);
    
    isPollingRef.current = true;
    setIsPolling(true);
    attemptsRef.current = 0;
    let timeoutId = null;
    let isCancelled = false;
    
    const poll = () => {
      // Critical fix: Check isCancelled flag instead of isPolling state
      if (isCancelled) {
        console.log("🛑 Polling cancelled via flag");
        return;
      }
      
      const currentAttempt = attemptsRef.current + 1;
      const timestamp = new Date().toISOString();
      console.log(`\n📡 [${timestamp}] Polling Attempt #${currentAttempt}`);
      console.log("   Order ID:", orderId);
      console.log("   Payment ID:", paymentId);
      
      Api.ProcessPaymentV4({ order_id: orderId, payment_id: paymentId })
        .then(({ data }) => {
          if (isCancelled) {
            console.log("   🛑 Response received but polling was cancelled");
            return;
          }
          
          if (!data) {
            console.warn("   ⚠️ No data in response");
            return;
          }
          
          console.log("   ✅ Response received:");
          console.log("   └─ Status:", data.status);
          console.log("   └─ Is Purchase Done:", data.is_purchase_done);
          console.log("   └─ Amount:", data.amount || data.final_amount_in_inr);
          console.log("   └─ Gateway:", data.gateway);
          
          if (data.amount) setAmount(data.amount);
          
          // Case-insensitive status comparison for robustness
          const status = (data.status || '').toLowerCase();
          console.log("   🔍 Normalized Status:", status);
          
          if (status === 'paid' && data.is_purchase_done) {
            console.log("   ✅ Payment Status: SUCCESS");
            console.log("   🎉 Stopping polling and showing success UI");
            isPollingRef.current = false;
            setIsPolling(false);
            setPaymentState('payment_success');
            setStatusMsg('Payment successful!');
            
            // Small delay before navigation for better UX
            setTimeout(() => {
              console.log("   🚀 Triggering onSuccess callback");
              onSuccess?.();
            }, 800);
          } else if (status === 'failed' || status === 'fail') {
            console.log("   ❌ Payment Status: FAILED");
            console.log("   🛑 Stopping polling and showing failure UI");
            isPollingRef.current = false;
            setIsPolling(false);
            setPaymentState('payment_failed');
            setStatusMsg('Payment failed. Please try again.');
            
            if (!isMobile) {
              console.log("   🖥️ Desktop: Will auto-close drawer in 3 seconds");
              setTimeout(() => {
                onClose?.();
              }, 3000);
            }
          } else {
            console.log("   ⏳ Payment Status: PENDING - Continuing to poll");
            attemptsRef.current += 1;
            console.log("   📊 Total attempts so far:", attemptsRef.current);
            
            // Show pending state after 20 attempts
            if (attemptsRef.current >= 20) {
              console.log("   ⚠️ 20+ attempts reached, showing pending warning UI");
              setPaymentState('payment_pending');
            }
            
            const delay = Math.min(2000 * Math.pow(1.2, attemptsRef.current), 6000);
            console.log(`   ⏰ Next poll scheduled in ${delay}ms (${(delay/1000).toFixed(1)}s)`);
            console.log(`   📈 Delay calculation: 2000 * 1.2^${attemptsRef.current} = ${2000 * Math.pow(1.2, attemptsRef.current)} → capped at ${delay}ms`);
            
            if (attemptsRef.current < 30 && !isCancelled) {
              timeoutId = setTimeout(poll, delay);
              console.log(`   ✓ Timeout ID ${timeoutId} scheduled`);
            } else {
              console.log("   🛑 Max polling attempts (30) reached. Stopping.");
              isPollingRef.current = false;
              setIsPolling(false);
              setPaymentState('payment_pending');
            }
          }
        })
        .catch((err) => {
          if (isCancelled) {
            console.log("   🛑 Error received but polling was cancelled");
            return;
          }
          
          console.error(`   ❌ Polling Error on attempt ${currentAttempt}:`, err.message || err);
          console.error("   Error details:", {
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data
          });
          
          attemptsRef.current += 1;
          if (attemptsRef.current < 10 && !isCancelled) {
            console.log(`   🔄 Retrying in 2000ms (${10 - attemptsRef.current} attempts remaining on error)`);
            timeoutId = setTimeout(poll, 2000);
          } else {
            console.error("   🛑 Max error attempts (10) reached. Stopping.");
            isPollingRef.current = false;
            setIsPolling(false);
          }
        });
    };
    
    console.log("⏰ Scheduling initial poll in 800ms");
    timeoutId = setTimeout(poll, 800);
    console.log("✓ Initial timeout scheduled with ID:", timeoutId);
    
    return () => {
      console.log("\n🧹 Cleanup function called");
      console.log("   Setting isCancelled = true");
      console.log("   Setting isPollingRef.current = false");
      console.log("   Clearing timeout ID:", timeoutId);
      isCancelled = true;
      isPollingRef.current = false;
      if (timeoutId) clearTimeout(timeoutId);
      setIsPolling(false);
      console.log("✓ Cleanup complete");
    };
  }, [visible, orderId, paymentId]);

  // Countdown timer (visual urgency)
  useEffect(() => {
    if (!visible) return;
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(prev => Math.max(prev - 1, 0)), 1000);
    return () => clearTimeout(t);
  }, [visible, countdown]);

  const qrImageSrc = useMemo(() => {
    if (!paymentOptions) return null;
    const qr = paymentOptions.qr_data || paymentOptions.upi_qr_base64 || paymentOptions.qrImage || null;
    if (!qr) return null;
    const isBase64 = typeof qr === 'string' && !qr.startsWith('http');
    return isBase64 ? `data:image/png;base64,${qr}` : qr;
  }, [paymentOptions]);

  const baseIntent = paymentOptions?.intent_url || paymentOptions?.upi_intent_url || paymentOptions?.intent?.upi || paymentOptions?.upi_intent || null;
  const phonePeIntent = useMemo(() => buildAppIntentUrl(baseIntent, 'phonepe'), [baseIntent]);
  const gpayIntent = useMemo(() => buildAppIntentUrl(baseIntent, 'gpay'), [baseIntent]);
  const paytmIntent = useMemo(() => buildAppIntentUrl(baseIntent, 'paytm'), [baseIntent]);
  const bhimIntent = useMemo(() => buildAppIntentUrl(baseIntent, 'bhim'), [baseIntent]);
  const appTiles = useMemo(() => [
    { name: 'UPI', logo: LOGO_UPI, href: baseIntent, targetApp: 'UPI_GENERIC' },
    { name: 'PhonePe', logo: LOGO_PHONEPE, href: phonePeIntent, targetApp: 'PHONEPE' },
    { name: 'Google Pay', logo: LOGO_GPAY, href: gpayIntent, targetApp: 'GPAY' },
    { name: 'Paytm', logo: LOGO_PAYTM, href: paytmIntent, targetApp: 'PAYTM' },
    { name: 'BHIM', logo: LOGO_UPI, href: bhimIntent, targetApp: 'BHIM' },
  ].filter(t => !!t.href), [baseIntent, phonePeIntent, gpayIntent, paytmIntent, bhimIntent]);

  // Function to handle app click and log the target app
  const handleAppClick = (tile) => {
    console.log("=== UPI App Selected ===");
    console.log("App Name:", tile.name);
    console.log("Target App:", tile.targetApp);
    console.log("Device OS:", "web");
    console.log("Payment Mode:", "UPI_INTENT");
    console.log("Order ID:", orderId);
    console.log("Intent URL:", tile.href);
    
    // Update UI state
    setPaymentState('payment_in_progress');
    setLastClickedApp(tile.name);
    setHasReturnedFromApp(false);
    setStatusMsg(`Completing payment in ${tile.name}...`);
    
    // Optional: Send this info to backend to track which app was used
    // You can implement this if your backend needs to know which UPI app the user selected
    /*
    Api.UpdatePaymentNotes({
      payment_id: paymentId,
      notes: {
        target_app: tile.targetApp,
        app_name: tile.name
      }
    }).catch(err => console.error("Failed to update payment notes:", err));
    */
  };
  
  // Handle retry payment (reset to initial state)
  const handleRetryPayment = () => {
    setPaymentState('initial');
    setLastClickedApp(null);
    setHasReturnedFromApp(false);
    setStatusMsg('Select your UPI app to retry');
    attemptsRef.current = 0;
  };
  
  // Handle manual status check
  const handleManualCheck = () => {
    console.log("Manual check requested");
    setPaymentState('checking_status');
    setStatusMsg('Checking payment status...');
    // Note: Don't trigger new polling here, existing polling loop handles it
    // Just changing state to show loading UI
  };
  
  // Handle retry opening the same app
  const handleRetryAppOpen = () => {
    if (!lastClickedApp) return;
    setStatusMsg(`Opening ${lastClickedApp} again...`);
    // The link will be clicked again by user
  };

  // Log drawer state when it opens - ONLY ONCE per open
  useEffect(() => {
    if (!visible || !paymentOptions) return;
    
    console.log("=== PhonePe Drawer Opened ===");
    console.log("Device:", isMobile ? "Mobile" : "Desktop");
    console.log("Order ID:", orderId);
    console.log("Payment ID:", paymentId);
    console.log("Amount:", amount);
    console.log("Payment Options:", {
      has_qr_data: !!qrImageSrc,
      has_intent_url: !!baseIntent,
      web: paymentOptions.web,
      sdk: paymentOptions.sdk,
      upi_qr: paymentOptions.upi_qr
    });
    console.log("Available UPI Apps:", appTiles.map(t => t.name).join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, paymentId]); // Only log when drawer opens or payment ID changes (intentionally limited deps)

  if (!visible) return null;

  const mm = Math.floor(countdown / 60).toString().padStart(2, '0');
  const ss = Math.floor(countdown % 60).toString().padStart(2, '0');

  const handleClose = () => {
    setIsPolling(false);
    if (paymentId) {
      // Best-effort cancel; don't block UI
      Api.CancelPayment({ payment_id: paymentId }).catch(() => {});
    }
    onClose?.();
  };

  const overlayStyle = isMobile
    ? { ...overlayBase, alignItems: 'flex-end' }
    : { ...overlayBase, alignItems: 'center' };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      {/* Drawer container for mobile; centered card for desktop */}
      <div style={isMobile ? drawer : card}>
        <div style={headerRow}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{amount ? `₹${amount}` : ''}</div>
          <button onClick={handleClose} aria-label="Close" style={closeBtn}>✕</button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 6, fontWeight: 600 }}>{statusMsg}</div>

        {/* Desktop QR block */}
        {!isMobile && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {qrImageSrc && <img src={qrImageSrc} alt="UPI QR" style={{ width: 280, height: 280, borderRadius: 12 }} />}
            {!qrImageSrc && (
              <div style={{ opacity: 0.8 }}>
                Generating QR...
                <div style={{ marginTop: 8, fontSize: 12 }}>Tip: If this persists, try clicking a UPI button below to open your app.</div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <img src={LOGO_GPAY} alt="Google Pay" style={logoRow} />
              <img src={LOGO_PHONEPE} alt="PhonePe" style={logoRow} />
              <img src={LOGO_UPI} alt="UPI" style={logoRow} />
            </div>
            <div style={{ opacity: 0.7, fontSize: 12, marginTop: 6 }}>...or any UPI app</div>
            <div style={{ marginTop: 8, fontWeight: 700 }}>{mm}:{ss}</div>
          </div>
        )}

        {/* Mobile actions */}
        {isMobile && (
          <div style={{ marginTop: 16 }}>
            {/* Initial State: Show UPI Apps */}
            {paymentState === 'initial' && (
              <>
                {appTiles.length ? (
                  <div style={gridContainer}>
                    {appTiles.map(tile => (
                      <a 
                        key={tile.name} 
                        href={tile.href} 
                        onClick={() => handleAppClick(tile)}
                        aria-label={`Open in ${tile.name}`} 
                        style={gridItem}
                      >
                        <img src={tile.logo} alt={tile.name} style={logoLg} />
                        <div style={tileCaption}>{tile.name}</div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div style={{ ...primaryBtn, pointerEvents: 'none', opacity: 0.6, justifyContent: 'center' }}>
                    <img src={LOGO_UPI} alt="UPI" style={logoSm} /> Getting payment options...
                  </div>
                )}
                <div style={{ textAlign: 'center', opacity: 0.7, fontSize: 12, marginTop: 12 }}>
                  You'll be redirected once payment is processed • {mm}:{ss}
                </div>
              </>
            )}

            {/* Payment in Progress State: Show Instructions */}
            {paymentState === 'payment_in_progress' && (
              <div style={instructionContainer}>
                <div style={loadingSpinnerStyle}>⟳</div>
                <div style={instructionBox}>
                  <div style={instructionStep}>✓ Complete payment in {lastClickedApp}</div>
                  <div style={instructionStep}>✓ Return here to confirm</div>
                </div>
                <div style={{ marginTop: 16, fontSize: 13, opacity: 0.7, textAlign: 'center' }}>
                  Don't see the app? <a href={baseIntent} style={linkStyle} onClick={handleRetryAppOpen}>Open Again</a>
                </div>
                <div style={actionButtonsContainer}>
                  <button onClick={handleManualCheck} style={secondaryBtn}>
                    Check Status Now
                  </button>
                  <button onClick={handleRetryPayment} style={primaryBtnOutline}>
                    Try Different App
                  </button>
                </div>
              </div>
            )}

            {/* Checking Status State: Show Loader */}
            {paymentState === 'checking_status' && (
              <div style={checkingStatusContainer}>
                <div style={loadingSpinnerStyle}>⟳</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginTop: 12, color: '#121212' }}>
                  Checking payment status...
                </div>
                <div style={{ fontSize: 13, opacity: 0.7, marginTop: 8, color: '#666' }}>
                  Please wait while we verify your payment
                </div>
                <div style={progressBarContainer}>
                  <div style={{...progressBarFill, width: `${(attemptsRef.current / 30) * 100}%`}} />
                </div>
              </div>
            )}

            {/* Payment Pending State: Show Warning */}
            {paymentState === 'payment_pending' && (
              <div style={warningContainer}>
                <div style={{ fontSize: 20, marginBottom: 8 }}>⏳</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                  Payment verification taking longer than usual
                </div>
                <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 16 }}>
                  This can happen due to network delays. Please wait or try again.
                </div>
                <div style={actionButtonsContainer}>
                  <button onClick={handleManualCheck} style={secondaryBtn}>
                    Keep Waiting
                  </button>
                  <button onClick={handleRetryPayment} style={primaryBtnOutline}>
                    Start New Payment
                  </button>
                </div>
                <button 
                  onClick={() => window.open('https://wa.me/919148477518', '_blank')} 
                  style={supportLink}
                >
                  Contact Support
                </button>
              </div>
            )}

            {/* Payment Success State: Show Success Message */}
            {paymentState === 'payment_success' && (
              <div style={successContainer}>
                <div style={successIconStyle}>✓</div>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 16, color: '#17CE3A' }}>
                  Payment Successful!
                </div>
                <div style={{ fontSize: 14, opacity: 0.8, marginTop: 8, color: '#2D3748' }}>
                  Redirecting you to order confirmation...
                </div>
                <div style={progressBarContainer}>
                  <div style={{...progressBarFill, width: '100%'}} />
                </div>
              </div>
            )}

            {/* Payment Failed State: Show Error Message */}
            {paymentState === 'payment_failed' && (
              <div style={failureContainer}>
                <div style={failureIconStyle}>✕</div>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 16, color: '#F44336' }}>
                  Payment Failed
                </div>
                <div style={{ fontSize: 14, opacity: 0.8, marginTop: 8, color: '#2D3748' }}>
                  Don't worry, no money was deducted. Please try again.
                </div>
                <div style={actionButtonsContainer}>
                  <button onClick={handleRetryPayment} style={secondaryBtn}>
                    Try Again
                  </button>
                  <button 
                    onClick={() => window.open('https://wa.me/919148477518', '_blank')} 
                    style={primaryBtnOutline}
                  >
                    Get Help
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const overlayBase = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center',
  zIndex: 1000
};

const drawer = {
  width: '100%', background: '#F6FAF6', color: '#121212', borderTopLeftRadius: 16, borderTopRightRadius: 16,
  padding: 16
};

const card = {
  width: 'min(420px, 92%)', margin: 'auto', background: '#F6FAF6', color: '#121212', borderRadius: 16,
  padding: 16, alignSelf: 'center'
};

const headerRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const closeBtn = { background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' };
const logoRow = { width: 28, height: 28, objectFit: 'contain' };
const logoSm = { width: 22, height: 22, objectFit: 'contain' };
const logoLg = { width: 40, height: 40, objectFit: 'contain' };
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textDecoration: 'none', background: '#17CE3A', color: '#121212', padding: '12px 14px', borderRadius: 10, fontWeight: 800 };
const gridContainer = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 };
const gridItem = { display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', background: '#fff', borderRadius: 12, padding: 12, border: '1px solid rgba(0,0,0,0.08)', color: '#121212' };
const tileCaption = { fontSize: 11, marginTop: 6, opacity: 0.8 };

// New styles for payment states - Fleek Design System
const instructionContainer = {
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FFF9 100%)',
  borderRadius: 16,
  padding: 24,
  textAlign: 'center',
  border: '1px solid #E8F5E9',
  boxShadow: '0 2px 8px rgba(23, 206, 58, 0.08)'
};

const checkingStatusContainer = {
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FFF1 100%)',
  borderRadius: 16,
  padding: 28,
  textAlign: 'center',
  border: '1px solid #E8F5E9',
  boxShadow: '0 4px 12px rgba(23, 206, 58, 0.12)'
};

const loadingSpinnerStyle = {
  fontSize: 40,
  animation: 'rotate 2s linear infinite',
  display: 'inline-block',
  color: '#17CE3A',
  fontWeight: 'bold'
};

const instructionBox = {
  background: '#F0FFF1',
  borderRadius: 12,
  padding: 18,
  marginTop: 16,
  textAlign: 'left',
  border: '1px solid rgba(23, 206, 58, 0.15)'
};

const instructionStep = {
  fontSize: 14,
  marginBottom: 10,
  color: '#2D3748',
  fontWeight: 600,
  lineHeight: '1.5'
};

const actionButtonsContainer = {
  display: 'flex',
  gap: 10,
  marginTop: 20
};

const secondaryBtn = {
  flex: 1,
  background: 'linear-gradient(135deg, #17CE3A 0%, #13B330 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  padding: '14px 18px',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(23, 206, 58, 0.25)',
  transition: 'all 0.2s ease'
};

const primaryBtnOutline = {
  flex: 1,
  background: '#fff',
  color: '#17CE3A',
  border: '2px solid #17CE3A',
  borderRadius: 12,
  padding: '14px 18px',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.2s ease'
};

const linkStyle = {
  color: '#17CE3A',
  textDecoration: 'none',
  cursor: 'pointer',
  fontWeight: 700,
  borderBottom: '2px solid rgba(23, 206, 58, 0.3)',
  transition: 'border-color 0.2s ease'
};

const warningContainer = {
  background: 'linear-gradient(135deg, #FFFBF0 0%, #FFF9E6 100%)',
  borderRadius: 16,
  padding: 24,
  textAlign: 'center',
  border: '1px solid #FFE8B3',
  boxShadow: '0 2px 8px rgba(255, 193, 7, 0.12)'
};

const progressBarContainer = {
  width: '100%',
  height: 6,
  background: '#E8F5E9',
  borderRadius: 10,
  marginTop: 16,
  overflow: 'hidden',
  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
};

const progressBarFill = {
  height: '100%',
  background: 'linear-gradient(90deg, #17CE3A 0%, #13B330 100%)',
  transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  borderRadius: 10,
  boxShadow: '0 0 8px rgba(23, 206, 58, 0.4)'
};

const supportLink = {
  background: 'transparent',
  color: '#FF9800',
  border: 'none',
  fontSize: 14,
  marginTop: 14,
  cursor: 'pointer',
  textDecoration: 'none',
  fontWeight: 700,
  borderBottom: '2px solid rgba(255, 152, 0, 0.3)'
};

const successContainer = {
  background: 'linear-gradient(135deg, #F0FFF1 0%, #E8F5E9 100%)',
  borderRadius: 16,
  padding: 32,
  textAlign: 'center',
  border: '2px solid #17CE3A',
  boxShadow: '0 4px 16px rgba(23, 206, 58, 0.2)'
};

const successIconStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #17CE3A 0%, #13B330 100%)',
  color: '#fff',
  fontSize: 36,
  fontWeight: 'bold',
  boxShadow: '0 4px 12px rgba(23, 206, 58, 0.3)'
};

const failureContainer = {
  background: 'linear-gradient(135deg, #FFF5F5 0%, #FFEBEE 100%)',
  borderRadius: 16,
  padding: 32,
  textAlign: 'center',
  border: '2px solid #F44336',
  boxShadow: '0 4px 16px rgba(244, 67, 54, 0.2)'
};

const failureIconStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
  color: '#fff',
  fontSize: 36,
  fontWeight: 'bold',
  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
};

export default PhonePeDrawer;

